import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CpuIcon } from '@phosphor-icons/react/dist/csr/Cpu';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { PackageIcon } from '@phosphor-icons/react/dist/csr/Package';
import { XCircleIcon } from '@phosphor-icons/react/dist/csr/XCircle';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { DispenseDialog } from './DispenseDialog';
import { RejectDialog } from './RejectDialog';
import { DEVICE_TYPE_LABEL_KEY, STATUS_LABEL_KEY } from './replacementLabels';
import { Button } from '../../design-system/primitives/Button/Button';
import { ApiError } from '../../lib/api/client';
import { replacementsApi, type ReplacementRow } from '../../lib/api/replacements';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './ReplacementDetail.module.css';

/**
 * The replacement detail view (M-13, Task 5, spec §5) — the request, the
 * linked case, the old device (linked when the serial resolved, plain text
 * when it did not) and the fulfilment outcome with its actor and time.
 *
 * That last part is the whole point of the milestone. The legacy recorded
 * NOWHERE what the patient was actually given: marking a replacement
 * `'completed'` (`admin-show-active-replacements.php:34`) wrote two columns
 * on one row and touched nothing else, so the system could not answer "what
 * device does this patient have".
 *
 * ## Actions come from the server's state, never re-derived here
 *
 * The Dispense/Reject buttons are gated ENTIRELY on `record.can.dispense`/
 * `record.can.reject` — booleans `ReplacementResource` computes server-side
 * from BOTH halves the server enforces: the `replacement.fulfil` permission
 * AND the terminal-state rule `ReplacementTransition` guards with a `409`.
 * Nothing here re-derives either from `permissions` + `status`. M-11
 * shipped a resource with no capability field and every decider got an
 * Answer button that 403'd.
 *
 * A TERMINAL ROW THEREFORE SHOWS NO BUTTON — and the `409` still exists on
 * the server, because hiding a button is not a guard.
 *
 * ## The old serial is shown whether or not it resolved (spec R-5)
 *
 * `old_serial_number` is the string the agent read off a broken device over
 * a phone line. `old_device` is the registered device it matched, or null.
 * Both are rendered: the serial always, and the device's own status beside
 * it only when there is one. Refusing the request because the serial was
 * illegible would have been worse than the missing link, so the missing
 * link is a normal state, not an error.
 *
 * ## The panel heading differs from the shell's `<h1>Replacements</h1>`
 *
 * `replacements.detailTitle` names the record (its case number), not the
 * module — the identical defect Cases, Requests and Escalations have each
 * shipped.
 */

export interface ReplacementDetailProps {
  uuid: string;
  onBack: () => void;
  /** Called after a dispense or a reject, so the queue behind this view refetches — a fulfilled row has left the default `requested` queue. */
  onChanged?: () => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function ReplacementDetail({ uuid, onBack, onChanged }: ReplacementDetailProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [record, setRecord] = useState<ReplacementRow | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [dispenseOpen, setDispenseOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const found = await replacementsApi.get(uuid);
      setRecord(found);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [uuid]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleActionDone(updated: ReplacementRow): void {
    setRecord(updated);
    setDispenseOpen(false);
    setRejectOpen(false);
    onChanged?.();
  }

  if (status === 'loading') {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('replacements.back')}
        </Button>
        <p role="status">{t('list.loading')}</p>
      </div>
    );
  }

  if (status === 'error' || record === null) {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('replacements.back')}
        </Button>
        <div role="alert" className={styles['recovery']}>
          <p>{t('replacements.loadFailedDetail')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>
          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="replacementDetailRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      </div>
    );
  }

  const offerDispense = record.can.dispense;
  const offerReject = record.can.reject;

  return (
    <div className={styles['page']}>
      <div className={styles['header']}>
        <div className={styles['identity']}>
          <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
            {t('replacements.back')}
          </Button>
          {/* `replacements.detailTitle`, NOT `replacements.title` — see this
              file's own docblock. */}
          <h2 className={styles['title']}>
            {t('replacements.detailTitle', { caseNumber: record.case.case_number })}
          </h2>
        </div>

        {offerDispense || offerReject ? (
          <div className={styles['actions']}>
            {offerDispense ? (
              <Button
                onClick={() => {
                  setDispenseOpen(true);
                }}
                icon={<PackageIcon size={16} />}
              >
                {t('replacements.dispenseAction')}
              </Button>
            ) : null}
            {offerReject ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setRejectOpen(true);
                }}
                icon={<XCircleIcon size={16} />}
              >
                {t('replacements.rejectAction')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <dl className={[styles['panel'], styles['primary'], styles['fields']].join(' ')}>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('replacements.fieldCase')}</dt>
          <dd className={styles['fieldValue']}>{record.case.case_number}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('replacements.fieldDeviceType')}</dt>
          <dd className={styles['fieldValue']}>{t(DEVICE_TYPE_LABEL_KEY[record.device_type])}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.status')}</dt>
          <dd className={styles['fieldValue']}>{t(STATUS_LABEL_KEY[record.status])}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('replacements.fieldRequestedAt')}</dt>
          <dd className={styles['fieldValue']}>
            {record.requested_at === null
              ? t('table.notRecorded')
              : formatTimestamp(record.requested_at, i18n.language)}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('replacements.fieldLibreType')}</dt>
          <dd className={styles['fieldValue']}>{record.libre_type ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('replacements.fieldPerceptionCode')}</dt>
          <dd className={styles['fieldValue']}>
            {record.perception_code ?? t('table.notRecorded')}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('replacements.fieldAssessmentCode')}</dt>
          <dd className={styles['fieldValue']}>
            {record.assessment_code ?? t('table.notRecorded')}
          </dd>
        </div>
        {record.reason === null ? null : (
          <div className={`${styles['field']} ${styles['fieldWide']}`}>
            <dt className={styles['fieldLabel']}>{t('replacements.fieldReason')}</dt>
            <dd className={styles['fieldValue']}>{record.reason}</dd>
          </div>
        )}
      </dl>

      {/* THE FAULTY DEVICE. The serial is shown whether or not it resolved
          to a registered one — see this file's own docblock (spec R-5). */}
      <section className={[styles['panel'], styles['section']].join(' ')}>
        <CardHead level="h3" icon={<CpuIcon size={16} weight="duotone" />} title={t('replacements.oldDeviceSectionTitle')} />
        <dl className={styles['fields']}>
          <div className={styles['field']}>
            <dt className={styles['fieldLabel']}>{t('replacements.fieldOldSerial')}</dt>
            <dd className={styles['fieldValue']}>
              <bdi dir="ltr">{record.old_serial_number ?? t('table.notRecorded')}</bdi>
            </dd>
          </div>
          <div className={styles['field']}>
            <dt className={styles['fieldLabel']}>{t('replacements.fieldRegisteredDevice')}</dt>
            <dd className={styles['fieldValue']}>
              {record.old_device === null
                ? t('replacements.serialUnmatched')
                : record.old_device.status}
            </dd>
          </div>
        </dl>
      </section>

      {/* THE FULFILMENT. What the patient was actually given, which the
          legacy recorded nowhere at all. */}
      <section className={[styles['panel'], styles['section']].join(' ')}>
        <CardHead level="h3" icon={<PackageIcon size={16} weight="duotone" />} title={t('replacements.outcomeSectionTitle')} />
        {record.status === 'requested' ? (
          <p className={styles['sectionEmpty']}>{t('replacements.noOutcomeYet')}</p>
        ) : record.status === 'dispensed' ? (
          <dl className={styles['fields']}>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('replacements.fieldNewDevice')}</dt>
              <dd className={styles['fieldValue']}>
                {/*
                  `new_serial_number` FIRST, the registry row only as a
                  fallback. A dispensed serial usually names no registered
                  device — every migrated replacement does — and reading
                  `new_device` alone printed "Not recorded" over a row that
                  records exactly what was handed over.
                */}
                <bdi dir="ltr">
                  {record.new_serial_number ??
                    record.new_device?.serial_number ??
                    t('table.notRecorded')}
                </bdi>
              </dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>
                {t('replacements.fieldFulfillingDispensary')}
              </dt>
              <dd className={styles['fieldValue']}>
                {record.fulfilling_dispensary?.name_en ?? t('table.notRecorded')}
              </dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('replacements.fieldDispensedBy')}</dt>
              <dd className={styles['fieldValue']}>
                {record.dispensed_by?.full_name ?? t('table.notRecorded')}
              </dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('replacements.fieldDispensedAt')}</dt>
              <dd className={styles['fieldValue']}>
                {record.dispensed_at === null
                  ? t('table.notRecorded')
                  : formatTimestamp(record.dispensed_at, i18n.language)}
              </dd>
            </div>
          </dl>
        ) : (
          <dl className={styles['fields']}>
            <div className={`${styles['field']} ${styles['fieldWide']}`}>
              <dt className={styles['fieldLabel']}>{t('replacements.fieldRejectionReason')}</dt>
              <dd className={styles['fieldValue']}>
                {record.rejection_reason ?? t('table.notRecorded')}
              </dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('replacements.fieldRejectedBy')}</dt>
              <dd className={styles['fieldValue']}>
                {record.rejected_by?.full_name ?? t('table.notRecorded')}
              </dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('replacements.fieldRejectedAt')}</dt>
              <dd className={styles['fieldValue']}>
                {record.rejected_at === null
                  ? t('table.notRecorded')
                  : formatTimestamp(record.rejected_at, i18n.language)}
              </dd>
            </div>
          </dl>
        )}
      </section>

      <DispenseDialog
        open={dispenseOpen}
        replacement={record}
        onClose={() => {
          setDispenseOpen(false);
        }}
        onDone={handleActionDone}
      />

      <RejectDialog
        open={rejectOpen}
        replacementUuid={record.uuid}
        caseNumber={record.case.case_number}
        onClose={() => {
          setRejectOpen(false);
        }}
        onDone={handleActionDone}
      />
    </div>
  );
}
